/**
 * Generic span-segment + slot-stabilization utility.
 *
 * Extracted from `CalendarLayoutService` so multiple grid implementations
 * (the calendar app's month/week view, the planning grid's schedule view,
 * future timeline views) can share the same multi-day rendering logic.
 *
 * The util is generic over any entry shape that has a stable `id` and an
 * optional `spanEnd` ISO date string. The owning component supplies a day
 * layout (one cell per day across one or more weeks) and the util fills
 * each cell's `segments` array, classifying each occurrence as one of
 * `'single' | 'start' | 'middle' | 'end'`, then stabilises slot indices so
 * a multi-day entry occupies the same vertical slot across the days and
 * weeks it covers.
 */

import { parseISO } from 'date-fns';

/** Minimum entry shape required by the span/slot algorithms. */
export interface SpannableEntry {
  /** Stable identifier. Used for slot-allocation memoisation and as a sort tiebreaker. */
  id: string;
  /**
   * Inclusive end date of the entry's span in `YYYY-MM-DD` form.
   * `undefined` means the entry is single-day (only renders in its key date cell).
   */
  spanEnd?: string;
}

/** Position of a particular cell within an entry's overall span. */
export type SpanSegmentType = 'start' | 'middle' | 'end' | 'single';

/** Per-cell rendering hint produced by `resolveSegments`. */
export interface SpanSegment<T extends SpannableEntry> {
  type: SpanSegmentType;
  entry: T;
}

/**
 * Minimal day-layout shape consumed by `stabilizeSlots`. Each day's
 * `segments` array may contain `null` placeholders that the algorithm uses
 * to keep multi-day entries on a consistent slot index.
 */
export interface SpannableDayLayout<T extends SpannableEntry> {
  date: string;
  segments: (SpanSegment<T> | null)[];
}

/**
 * Input shape for `resolveSegments`: a map of `YYYY-MM-DD` date keys to the
 * entries that *start* on that date. Matches the calendar lib's `CalendarData`
 * shape but generalised.
 */
export type SpannableEntryMap<T extends SpannableEntry> = Record<string, T[]>;

// ─── public API ────────────────────────────────────────────────────────

/**
 * Resolve all span segments for a given day cell.
 * Considers both entries that start on `dateKey` and multi-day spans that
 * began earlier and extend into this day.
 *
 * The returned array is stably sorted by start date ascending, then by
 * entry id ascending.
 */
export function resolveSegments<T extends SpannableEntry>(dateKey: string, day: Date, entries: SpannableEntryMap<T>): SpanSegment<T>[] {
  const entryStartDates = new Map<T, string>();
  const direct = resolveDirectEntries(dateKey, day, entries[dateKey], entryStartDates);
  const spanning = resolveSpanningEntries(dateKey, day, entries, entryStartDates);
  return sortSegmentsByStartDate([...direct, ...spanning], entryStartDates);
}

/**
 * Stabilise slot positions across a single row of days. Multi-day entries
 * get a consistent vertical slot across all days they span, with `null`
 * placeholders inserted to keep alignment. Mutates `days[i].segments`
 * in place.
 *
 * @param priorSlots Slot assignments carried over from the previous row
 *   (used by `stabilizeSlotsAcrossWeeks`). Entries with a preferred slot
 *   keep it when possible.
 */
export function stabilizeSlots<T extends SpannableEntry>(days: SpannableDayLayout<T>[], priorSlots: Map<string, number> = new Map()): void {
  const entryInfo = collectEntryInfo(days);
  if (entryInfo.size <= 1 && priorSlots.size === 0) return;

  const sorted = sortEntriesForSlotAllocation(entryInfo, priorSlots);
  const grid = allocateSlots(sorted, days.length, priorSlots);
  rebuildDaySegments(days, grid);
}

/**
 * Stabilise slot positions across multiple week rows. After stabilising
 * each week individually, carries the slot of every continuing entry from
 * the last day of week N into the first day of week N+1 so a span that
 * crosses the week boundary keeps the same vertical position.
 */
export function stabilizeSlotsAcrossWeeks<T extends SpannableEntry>(weeks: Array<{ days: SpannableDayLayout<T>[] }>): void {
  const carryOver = new Map<string, number>();

  for (const week of weeks) {
    stabilizeSlots(week.days, carryOver);

    carryOver.clear();
    const lastDay = week.days.at(-1);
    if (!lastDay) continue;

    for (let slot = 0; slot < lastDay.segments.length; slot++) {
      const seg = lastDay.segments[slot];
      if (seg && seg.type !== 'single' && seg.type !== 'end') {
        carryOver.set(seg.entry.id, slot);
      }
    }
  }
}

// ─── segment resolution ────────────────────────────────────────────────

function resolveDirectEntries<T extends SpannableEntry>(
  dateKey: string,
  day: Date,
  directEntries: T[] | undefined,
  entryStartDates: Map<T, string>
): SpanSegment<T>[] {
  if (!directEntries) return [];

  return directEntries.map(entry => {
    entryStartDates.set(entry, dateKey);
    return { type: classifyDirectEntry(entry, day), entry };
  });
}

function classifyDirectEntry<T extends SpannableEntry>(entry: T, day: Date): SpanSegmentType {
  if (!entry.spanEnd) return 'single';
  const spanEndDate = parseISO(entry.spanEnd);
  return day.getTime() === spanEndDate.getTime() ? 'single' : 'start';
}

function resolveSpanningEntries<T extends SpannableEntry>(
  dateKey: string,
  day: Date,
  entries: SpannableEntryMap<T>,
  entryStartDates: Map<T, string>
): SpanSegment<T>[] {
  const segments: SpanSegment<T>[] = [];

  for (const [startDateKey, dateEntries] of Object.entries(entries)) {
    if (startDateKey >= dateKey) continue;

    for (const entry of dateEntries) {
      const segment = classifySpanningEntry(entry, startDateKey, day);
      if (!segment) continue;

      segments.push({ type: segment, entry });
      entryStartDates.set(entry, startDateKey);
    }
  }

  return segments;
}

function classifySpanningEntry<T extends SpannableEntry>(entry: T, startDateKey: string, day: Date): SpanSegmentType | null {
  if (!entry.spanEnd) return null;

  const spanEndDate = parseISO(entry.spanEnd);
  const startDate = parseISO(startDateKey);

  if (day <= startDate || day > spanEndDate) return null;
  return day.getTime() === spanEndDate.getTime() ? 'end' : 'middle';
}

function sortSegmentsByStartDate<T extends SpannableEntry>(segments: SpanSegment<T>[], entryStartDates: Map<T, string>): SpanSegment<T>[] {
  return segments.sort((a, b) => {
    const aStart = entryStartDates.get(a.entry) ?? '';
    const bStart = entryStartDates.get(b.entry) ?? '';
    if (aStart !== bStart) return aStart.localeCompare(bStart);
    return a.entry.id.localeCompare(b.entry.id);
  });
}

// ─── slot allocation ───────────────────────────────────────────────────

interface EntryDayInfo {
  days: number[];
  isSpan: boolean;
  firstDay: number;
}

function collectEntryInfo<T extends SpannableEntry>(days: SpannableDayLayout<T>[]): Map<string, EntryDayInfo> {
  const entryInfo = new Map<string, EntryDayInfo>();

  for (let d = 0; d < days.length; d++) {
    for (const seg of days[d].segments) {
      if (!seg) continue;
      const info = entryInfo.get(seg.entry.id);
      if (info) {
        info.days.push(d);
      } else {
        entryInfo.set(seg.entry.id, { days: [d], isSpan: seg.type !== 'single', firstDay: d });
      }
    }
  }

  return entryInfo;
}

function sortEntriesForSlotAllocation(entryInfo: Map<string, EntryDayInfo>, priorSlots: Map<string, number>): [string, EntryDayInfo][] {
  return [...entryInfo.entries()].sort(([idA, a], [idB, b]) => {
    const priorA = priorSlots.get(idA);
    const priorB = priorSlots.get(idB);
    const hasPriorA = priorA !== undefined;
    const hasPriorB = priorB !== undefined;

    if (hasPriorA !== hasPriorB) return hasPriorA ? -1 : 1;
    if (hasPriorA && hasPriorB) return priorA - priorB;

    if (a.isSpan !== b.isSpan) return a.isSpan ? -1 : 1;
    if (a.firstDay !== b.firstDay) return a.firstDay - b.firstDay;
    if (a.days.length !== b.days.length) return b.days.length - a.days.length;
    return idA.localeCompare(idB);
  });
}

function allocateSlots(sorted: [string, EntryDayInfo][], dayCount: number, priorSlots: Map<string, number>): (string | undefined)[][] {
  const grid: (string | undefined)[][] = Array.from({ length: dayCount }, () => []);

  for (const [id, info] of sorted) {
    const slot = resolveSlot(grid, info.days, priorSlots.get(id));

    for (const d of info.days) {
      while (grid[d].length <= slot) grid[d].push(undefined);
      grid[d][slot] = id;
    }
  }

  return grid;
}

function resolveSlot(grid: (string | undefined)[][], entryDays: number[], preferredSlot: number | undefined): number {
  if (preferredSlot !== undefined && isSlotAvailable(grid, entryDays, preferredSlot)) {
    return preferredSlot;
  }
  return findLowestAvailableSlot(grid, entryDays);
}

function isSlotAvailable(grid: (string | undefined)[][], entryDays: number[], slot: number): boolean {
  return entryDays.every(d => grid[d][slot] === undefined);
}

function findLowestAvailableSlot(grid: (string | undefined)[][], days: number[]): number {
  for (let slot = 0; ; slot++) {
    if (isSlotAvailable(grid, days, slot)) return slot;
  }
}

function rebuildDaySegments<T extends SpannableEntry>(days: SpannableDayLayout<T>[], grid: (string | undefined)[][]): void {
  const maxSlots = Math.max(...grid.map(d => d.length));

  for (let d = 0; d < days.length; d++) {
    const segMap = buildSegmentMap(days[d].segments);
    days[d].segments = buildStabilizedSegments(grid[d], maxSlots, segMap);
  }
}

function buildSegmentMap<T extends SpannableEntry>(segments: (SpanSegment<T> | null)[]): Map<string, SpanSegment<T>> {
  const segMap = new Map<string, SpanSegment<T>>();
  for (const seg of segments) {
    if (seg) segMap.set(seg.entry.id, seg);
  }
  return segMap;
}

function buildStabilizedSegments<T extends SpannableEntry>(
  dayGrid: (string | undefined)[],
  maxSlots: number,
  segMap: Map<string, SpanSegment<T>>
): (SpanSegment<T> | null)[] {
  const stabilized: (SpanSegment<T> | null)[] = [];
  for (let s = 0; s < maxSlots; s++) {
    const id = dayGrid[s];
    stabilized.push(id === undefined ? null : (segMap.get(id) ?? null));
  }

  while (stabilized.length > 0 && stabilized.at(-1) === null) {
    stabilized.pop();
  }

  return stabilized;
}
